import { useMemo, useState } from 'react';

interface Transaction {
  id: string;
  amount: number;
  type: string;
  description: string | null;
  status: string;
  created_at: string;
}

interface Retainer {
  balance: number;
  total_funded: number;
  total_used: number;
}

interface ClientInfo {
  name: string;
  email: string;
  company?: string | null;
}

interface Props {
  transactions: Transaction[];
  retainer: Retainer | null;
  onFundRetainer: () => void;
  clientInfo?: ClientInfo;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const formatDateTime = (dateString: string) =>
  new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

type FilterType = 'all' | 'credit' | 'debit' | 'fund' | 'refund';

// Invoice print helper
const generateInvoiceHTML = (
  clientInfo: ClientInfo | undefined,
  transactions: Transaction[],
  retainer: Retainer | null,
): string => {
  const invoiceNum = `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const rowsHTML = transactions
    .slice()
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    .map((tx) => {
      const isCredit = tx.type === 'fund' || tx.type === 'credit' || tx.type === 'refund';
      const typeLabel: Record<string, string> = {
        fund: 'Retainer Funded',
        credit: 'Credit',
        debit: 'Attorney Fee / Expense',
        refund: 'Refund',
      };
      const date = new Date(tx.created_at).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
      const amtColor = isCredit ? '#166534' : '#991b1b';
      const amtPrefix = isCredit ? '+' : '-';
      const fmtAmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(tx.amount));
      return `<tr>
        <td style="padding:10px 12px;font-size:13px;color:#555;border-bottom:1px solid #eee;">${date}</td>
        <td style="padding:10px 12px;font-size:13px;color:#1a1a1a;border-bottom:1px solid #eee;">${(tx.description || typeLabel[tx.type] || tx.type).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>
        <td style="padding:10px 12px;font-size:12px;color:#777;border-bottom:1px solid #eee;text-transform:capitalize;">${typeLabel[tx.type] || tx.type}</td>
        <td style="padding:10px 12px;font-size:13px;font-weight:700;color:${amtColor};text-align:right;border-bottom:1px solid #eee;">${amtPrefix}${fmtAmt}</td>
        <td style="padding:10px 12px;font-size:12px;border-bottom:1px solid #eee;text-align:center;"><span style="background:${tx.status === 'completed' ? '#dcfce7' : '#fef9c3'};color:${tx.status === 'completed' ? '#166534' : '#713f12'};padding:2px 8px;border-radius:10px;">${tx.status}</span></td>
      </tr>`;
    })
    .join('');

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1.0"/>
  <title>Retainer Invoice — ${clientInfo?.name || 'Client'}</title>
  <style>
    body{margin:0;padding:0;font-family:Georgia,serif;background:#fff;color:#1a1a1a;}
    .page{max-width:760px;margin:0 auto;padding:48px 40px;}
    table{border-collapse:collapse;}
    @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
  </style>
</head>
<body>
  <div class="page">
    <!-- Header -->
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:36px;">
      <tr>
        <td style="vertical-align:top;">
          <p style="margin:0 0 2px;font-size:11px;letter-spacing:3px;color:#c9a84c;text-transform:uppercase;font-family:Arial,sans-serif;font-weight:700;">Serenity Law PLLC</p>
          <p style="margin:0 0 2px;font-size:13px;color:#555;font-family:Arial,sans-serif;">Tariff Refund Representation</p>
          <p style="margin:0 0 1px;font-size:12px;color:#888;font-family:Arial,sans-serif;">engage@tariffreturn.attorney</p>
          <p style="margin:0;font-size:12px;color:#888;font-family:Arial,sans-serif;">(509) 396-0741</p>
        </td>
        <td style="text-align:right;vertical-align:top;">
          <h1 style="margin:0 0 4px;font-size:32px;font-weight:normal;color:#1a1a1a;letter-spacing:-1px;">INVOICE</h1>
          <p style="margin:0 0 2px;font-size:13px;color:#555;font-family:Arial,sans-serif;">${invoiceNum}</p>
          <p style="margin:0;font-size:12px;color:#999;font-family:Arial,sans-serif;">Date: ${today}</p>
        </td>
      </tr>
    </table>

    <div style="height:3px;background:linear-gradient(90deg,#c9a84c,#e8d08a,#c9a84c);margin-bottom:32px;"></div>

    <!-- Bill To -->
    <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:32px;">
      <tr>
        <td style="vertical-align:top;width:50%;">
          <p style="margin:0 0 6px;font-size:10px;color:#999;text-transform:uppercase;letter-spacing:1.5px;font-family:Arial,sans-serif;">Bill To</p>
          <p style="margin:0 0 2px;font-size:16px;font-weight:bold;">${(clientInfo?.name || 'Client').replace(/</g, '&lt;')}</p>
          ${clientInfo?.company ? `<p style="margin:0 0 1px;font-size:13px;color:#555;font-family:Arial,sans-serif;">${clientInfo.company.replace(/</g, '&lt;')}</p>` : ''}
          <p style="margin:0;font-size:13px;color:#555;font-family:Arial,sans-serif;">${(clientInfo?.email || '').replace(/</g, '&lt;')}</p>
        </td>
        <td style="vertical-align:top;width:50%;text-align:right;">
          <p style="margin:0 0 6px;font-size:10px;color:#999;text-transform:uppercase;letter-spacing:1.5px;font-family:Arial,sans-serif;">Summary</p>
          <table cellpadding="0" cellspacing="0" style="margin-left:auto;">
            <tr>
              <td style="font-size:13px;color:#555;font-family:Arial,sans-serif;padding:2px 0;padding-right:20px;">Total Funded</td>
              <td style="font-size:13px;font-weight:bold;text-align:right;color:#166534;">${fmt(retainer?.total_funded ?? 0)}</td>
            </tr>
            <tr>
              <td style="font-size:13px;color:#555;font-family:Arial,sans-serif;padding:2px 0;padding-right:20px;">Total Used</td>
              <td style="font-size:13px;font-weight:bold;text-align:right;color:#991b1b;">${fmt(retainer?.total_used ?? 0)}</td>
            </tr>
            <tr>
              <td style="font-size:14px;color:#1a1a1a;font-family:Arial,sans-serif;padding:8px 0 2px;padding-right:20px;border-top:1px solid #ddd;font-weight:bold;">Current Balance</td>
              <td style="font-size:18px;font-weight:bold;text-align:right;color:#c9a84c;border-top:1px solid #ddd;padding-top:8px;">${fmt(retainer?.balance ?? 0)}</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>

    <!-- Transactions -->
    <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e8e0cc;border-radius:6px;overflow:hidden;margin-bottom:32px;">
      <thead>
        <tr style="background:#fafaf7;">
          <th style="padding:10px 12px;font-size:11px;color:#999;text-align:left;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Date</th>
          <th style="padding:10px 12px;font-size:11px;color:#999;text-align:left;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Description</th>
          <th style="padding:10px 12px;font-size:11px;color:#999;text-align:left;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Type</th>
          <th style="padding:10px 12px;font-size:11px;color:#999;text-align:right;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Amount</th>
          <th style="padding:10px 12px;font-size:11px;color:#999;text-align:center;font-family:Arial,sans-serif;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Status</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHTML || '<tr><td colspan="5" style="padding:24px;text-align:center;color:#bbb;font-family:Arial,sans-serif;">No transactions on record</td></tr>'}
      </tbody>
    </table>

    <!-- Footer -->
    <div style="margin-top:48px;padding-top:20px;border-top:1px solid #e8e0cc;text-align:center;">
      <p style="margin:0;font-size:11px;color:#999;font-family:Arial,sans-serif;">Serenity Law PLLC &bull; Tariff Refund Representation &bull; engage@tariffreturn.attorney &bull; (509) 396-0741</p>
      <p style="margin:4px 0 0;font-size:10px;color:#bbb;font-family:Arial,sans-serif;">This document is a retainer account statement. Confidential — Attorney-Client Privileged.</p>
    </div>
  </div>
</body>
</html>`;
};

export default function RetainerStatement({ transactions, retainer, onFundRetainer, clientInfo }: Props) {
  const [filter, setFilter] = useState<FilterType>('all');

  const sortedTransactions = useMemo(
    () => [...transactions].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
    [transactions]
  );

  // Calculate running balances (oldest first)
  const withRunningBalance = useMemo(() => {
    let running = 0;
    return sortedTransactions.map((tx) => {
      const isCredit = tx.type === 'credit' || tx.type === 'fund' || tx.type === 'refund';
      running = isCredit ? running + tx.amount : running - tx.amount;
      return { ...tx, runningBalance: running };
    });
  }, [sortedTransactions]);

  // Reverse for display (newest first)
  const displayTransactions = useMemo(
    () => [...withRunningBalance].reverse(),
    [withRunningBalance]
  );

  const filteredTransactions = useMemo(
    () =>
      filter === 'all'
        ? displayTransactions
        : displayTransactions.filter((tx) => tx.type === filter),
    [displayTransactions, filter]
  );

  const balance = retainer?.balance ?? 0;
  const totalFunded = retainer?.total_funded ?? 0;
  const totalUsed = retainer?.total_used ?? 0;

  const handleGenerateInvoice = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups in your browser to generate the invoice.');
      return;
    }
    printWindow.document.write(generateInvoiceHTML(clientInfo, transactions, retainer));
    printWindow.document.close();
    printWindow.focus();
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      fund: 'Retainer Funded',
      credit: 'Credit',
      debit: 'Attorney Fee / Expense',
      refund: 'Refund',
    };
    return labels[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      fund: 'ri-arrow-down-circle-fill',
      credit: 'ri-arrow-down-circle-fill',
      debit: 'ri-arrow-up-circle-fill',
      refund: 'ri-refund-2-fill',
    };
    return icons[type] || 'ri-exchange-line';
  };

  const getTypeColor = (type: string) => {
    if (type === 'fund' || type === 'credit' || type === 'refund') return 'text-green-400';
    if (type === 'debit') return 'text-red-400';
    return 'text-white/60';
  };

  const getTypeBg = (type: string) => {
    if (type === 'fund' || type === 'credit' || type === 'refund') return 'bg-green-500/10 border-green-500/20';
    if (type === 'debit') return 'bg-red-500/10 border-red-500/20';
    return 'bg-white/5 border-white/10';
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      pending: 'bg-[#FDB913]/20 text-[#FDB913] border-[#FDB913]/30',
      failed: 'bg-red-500/20 text-red-400 border-red-500/30',
      refunded: 'bg-green-500/20 text-green-400 border-green-500/30',
    };
    return styles[status] || styles.completed;
  };

  const isCredit = (type: string) => type === 'fund' || type === 'credit' || type === 'refund';

  return (
    <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-2xl font-black text-white">Retainer Statement</h3>
          <p className="text-white/40 text-xs mt-1 font-medium">
            Complete transaction history &amp; running balance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleGenerateInvoice}
            className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/20 text-white/70 hover:text-white font-bold text-sm px-4 py-2 rounded-lg transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-file-text-line"></i>
            Invoice
          </button>
          <button
            onClick={onFundRetainer}
            className="flex items-center gap-2 bg-[#FDB913]/10 hover:bg-[#FDB913]/20 border border-[#FDB913]/30 text-[#FDB913] font-bold text-sm px-4 py-2 rounded-lg transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-circle-line"></i>
            Add Funds
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-gradient-to-br from-[#FDB913]/15 to-[#FDB913]/5 border border-[#FDB913]/30 rounded-xl p-5 text-center">
          <p className="text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Current Balance</p>
          <p className="text-3xl font-black text-[#FDB913]">{formatCurrency(balance)}</p>
          <div className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-bold ${balance > 0 ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-white/10 text-white/30 border-white/10'}`}>
            <i className={balance > 0 ? 'ri-checkbox-circle-fill' : 'ri-time-line'}></i>
            {balance > 0 ? 'Funded' : 'Unfunded'}
          </div>
        </div>
        <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-5 text-center">
          <p className="text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Total Funded</p>
          <p className="text-3xl font-black text-green-400">{formatCurrency(totalFunded)}</p>
          <p className="text-white/30 text-xs mt-2">All-time deposits</p>
        </div>
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5 text-center">
          <p className="text-white/50 text-xs font-bold uppercase tracking-wider mb-2">Total Used</p>
          <p className="text-3xl font-black text-red-400">{formatCurrency(totalUsed)}</p>
          <p className="text-white/30 text-xs mt-2">Attorney fees &amp; costs</p>
        </div>
      </div>

      {/* Filter tabs */}
      {transactions.length > 0 && (
        <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg p-1 mb-5 w-fit flex-wrap">
          {(['all', 'fund', 'credit', 'debit', 'refund'] as FilterType[]).map((f) => {
            const labels: Record<FilterType, string> = {
              all: 'All',
              fund: 'Funded',
              credit: 'Credits',
              debit: 'Fees',
              refund: 'Refunds',
            };
            const count = f === 'all' ? transactions.length : transactions.filter((t) => t.type === f).length;
            if (f !== 'all' && count === 0) return null;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                  filter === f ? 'bg-[#FDB913] text-black' : 'text-white/50 hover:text-white'
                }`}
              >
                {labels[f]} {count > 0 && <span className="opacity-70">({count})</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Transaction list */}
      {filteredTransactions.length === 0 ? (
        <div className="text-center py-16 bg-white/5 border border-white/10 rounded-xl">
          <i className="ri-file-list-3-line text-white/20 text-5xl mb-4"></i>
          <p className="text-white/40 text-base font-bold">No transactions yet</p>
          <p className="text-white/25 text-sm mt-1">Your retainer activity will appear here once you add funds</p>
          <button
            onClick={onFundRetainer}
            className="mt-4 inline-flex items-center gap-2 bg-[#FDB913]/20 hover:bg-[#FDB913]/30 border border-[#FDB913]/40 text-[#FDB913] font-bold text-sm px-5 py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-circle-line"></i>
            Fund Your Retainer
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Column headers */}
          <div className="grid grid-cols-12 gap-3 px-4 pb-1">
            <div className="col-span-5 text-white/30 text-xs font-bold uppercase tracking-wider">Description</div>
            <div className="col-span-2 text-white/30 text-xs font-bold uppercase tracking-wider text-center">Date</div>
            <div className="col-span-2 text-white/30 text-xs font-bold uppercase tracking-wider text-right">Amount</div>
            <div className="col-span-2 text-white/30 text-xs font-bold uppercase tracking-wider text-right">Balance</div>
            <div className="col-span-1 text-white/30 text-xs font-bold uppercase tracking-wider text-center">Status</div>
          </div>

          {filteredTransactions.map((tx) => (
            <div
              key={tx.id}
              className={`grid grid-cols-12 gap-3 items-center px-4 py-3.5 rounded-xl border transition-all hover:bg-white/5 ${getTypeBg(tx.type)}`}
            >
              {/* Description */}
              <div className="col-span-5 flex items-center gap-3 min-w-0">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${isCredit(tx.type) ? 'bg-green-500/15' : 'bg-red-500/15'}`}>
                  <i className={`${getTypeIcon(tx.type)} ${getTypeColor(tx.type)} text-sm`}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-bold truncate">
                    {tx.description || getTypeLabel(tx.type)}
                  </p>
                  <p className="text-white/35 text-xs capitalize">{getTypeLabel(tx.type)}</p>
                </div>
              </div>

              {/* Date */}
              <div className="col-span-2 text-center">
                <p className="text-white/60 text-xs font-medium">{formatDate(tx.created_at)}</p>
                <p className="text-white/25 text-xs">{formatDateTime(tx.created_at).split(',')[2]?.trim()}</p>
              </div>

              {/* Amount */}
              <div className="col-span-2 text-right">
                <span className={`text-base font-black ${getTypeColor(tx.type)}`}>
                  {isCredit(tx.type) ? '+' : '-'}{formatCurrency(Math.abs(tx.amount))}
                </span>
              </div>

              {/* Running Balance */}
              <div className="col-span-2 text-right">
                <span className="text-white/70 text-sm font-bold">{formatCurrency(tx.runningBalance)}</span>
              </div>

              {/* Status */}
              <div className="col-span-1 flex justify-center">
                <span className={`inline-flex items-center border rounded-full px-2 py-0.5 text-xs font-black whitespace-nowrap ${getStatusBadge(tx.status)}`}>
                  {tx.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer note */}
      {transactions.length > 0 && (
        <div className="mt-5 flex items-start gap-2 bg-white/5 border border-white/10 rounded-lg p-4">
          <i className="ri-information-line text-white/30 text-sm flex-shrink-0 mt-0.5"></i>
          <p className="text-white/40 text-xs leading-relaxed">
            This statement reflects all retainer activity on your account. The running balance column shows your retainer balance after each transaction. For any discrepancies, contact your attorney at <strong className="text-white/50">engage@tariffreturn.attorney</strong>.
          </p>
        </div>
      )}
    </div>
  );
}
